/**
 * Checagem de versão da pista (revisão das Fases 1 e 2 do pedido de 19/10).
 *
 * O tablet com a aba aberta continua rodando o JS antigo até recarregar (o Service Worker só pega o
 * index novo na navegação). Com regra nova de desconto (prêmio do sorteio, progressivo), o JS antigo
 * mostra a tela errada. Aqui a pista compara o JS que está rodando com o publicado
 * (asset-manifest.json do build, sem cache) e recarrega SÓ entre um atendimento e outro.
 *
 * Segurança:
 *  - sem internet, sem o arquivo ou com resposta estranha: não faz nada;
 *  - recarrega uma vez por versão nova (sessionStorage): se o recarregamento não trouxer o JS novo,
 *    não entra em laço;
 *  - quem chama diz quando pode (tela da placa vazia, sem janela aberta); a condição é conferida de
 *    novo na hora de recarregar.
 */
import { useEffect, useRef } from "react";

const CHAVE = "versao_recarregada";
const MAIN_JS = /\/static\/js\/main\.[0-9a-f]+\.js$/i;
export const INTERVALO_CHECAGEM_MS = 10 * 60 * 1000;

function caminho(src) {
  try { return new URL(src, window.location.href).pathname; } catch { return null; }
}

/** Caminho do JS principal que está rodando (null fora do build: dev/testes). */
export function bundleAtual(doc = typeof document !== "undefined" ? document : null) {
  try {
    const scripts = Array.from(doc?.querySelectorAll("script[src]") || []);
    for (const el of scripts) {
      const p = caminho(el.getAttribute("src") || "");
      if (p && MAIN_JS.test(p)) return p;
    }
  } catch { /* sem DOM */ }
  return null;
}

/** Caminho do JS principal publicado (asset-manifest.json), ou null. */
export async function bundlePublicado(fetcher = typeof fetch !== "undefined" ? fetch : null) {
  if (!fetcher) return null;
  try {
    const r = await fetcher("/asset-manifest.json", { cache: "no-store", credentials: "same-origin" });
    if (!r || !r.ok) return null;
    const j = await r.json();
    const main = j?.files?.["main.js"];
    if (typeof main !== "string") return null;
    const p = caminho(main);
    return p && MAIN_JS.test(p) ? p : null;
  } catch { return null; }
}

function janelaAberta() {
  try {
    return !!document.querySelector('.modal-overlay, [role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"]');
  } catch { return false; }
}

/**
 * Recarrega a página quando há JS novo publicado. Devolve true quando pediu o recarregamento.
 * podeRecarregar: conferida na hora (o frentista pode ter começado um atendimento durante a consulta).
 */
export async function recarregarSeVersaoNova({ fetcher, recarregar, podeRecarregar } = {}) {
  try {
    if (typeof navigator !== "undefined" && navigator.onLine === false) return false;
    const atual = bundleAtual();
    if (!atual) return false;
    const publicado = await bundlePublicado(fetcher);
    if (!publicado || publicado === atual) return false;
    if (podeRecarregar && !podeRecarregar()) return false;
    if (janelaAberta()) return false;
    let jaTentou = null;
    try { jaTentou = sessionStorage.getItem(CHAVE); } catch { return false; } // sem sessionStorage não arrisca laço
    if (jaTentou === publicado) return false;
    try { sessionStorage.setItem(CHAVE, publicado); } catch { return false; }
    (recarregar || (() => window.location.reload()))();
    return true;
  } catch {
    return false;
  }
}

/**
 * Enquanto `ativo`: confere ao ficar ativo, quando a aba volta a ficar visível e a cada
 * INTERVALO_CHECAGEM_MS. Fora do build (sem o JS principal na página) não faz nada.
 */
export function useRecarregaSeVersaoNova(ativo, intervaloMs = INTERVALO_CHECAGEM_MS) {
  const ativoRef = useRef(ativo);
  ativoRef.current = ativo;
  useEffect(() => {
    if (!ativo || !bundleAtual()) return undefined;
    const checar = () => {
      if (typeof document !== "undefined" && document.visibilityState === "hidden") return;
      recarregarSeVersaoNova({ podeRecarregar: () => ativoRef.current });
    };
    const primeira = setTimeout(checar, 1500);
    const t = setInterval(checar, intervaloMs);
    const onVis = () => { if (document.visibilityState === "visible") checar(); };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      clearTimeout(primeira);
      clearInterval(t);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [ativo, intervaloMs]);
}
